import { Check, Circle } from 'lucide-react';
import { cn } from '@pixlide/ui';
import { PASSWORD_RULES } from '@/features/auth/password-rules';

/**
 * Retour en direct sur la robustesse du mot de passe.
 *
 * Les règles viennent de `password-rules.ts`, le MÊME module que celui qui valide les
 * formulaires (`schemas.ts`) : ce qui est affiché ici est exactement ce qui bloque la
 * soumission, jamais un sur-ensemble décoratif.
 *
 * Elles suivent le NIST SP 800-63B : la LONGUEUR prime, et aucune règle de composition
 * (« une majuscule, un chiffre, un caractère spécial ») n'est imposée. Ces règles
 * produisent en pratique « Password1! » — conforme et trivial à casser — tout en poussant
 * les utilisateurs à réutiliser leurs mots de passe d'un service à l'autre.
 *
 * À NOTER : la maquette affiche « au moins 8 caractères, une majuscule, un chiffre ou un
 * symbole ». Ce sont les RÈGLES qui n'ont pas été reprises, pas la présentation : elles
 * sont plus faibles que celles appliquées par l'API, et un formulaire qui annonce une
 * politique que le serveur refuse ensuite est un refus garanti à la soumission.
 */

/** Couleur de la jauge : rouge tant que le compte n'y est pas, vert une fois tout rempli. */
const METER_TONES = [
  'bg-danger-500',
  'bg-danger-500',
  'bg-danger-500',
  'bg-warning-500',
  'bg-success-500',
] as const;

export function PasswordPolicy({ password, email }: { password: string; email: string }) {
  if (password.length === 0) {
    return (
      <p className="text-xs text-ink-500">
        Conseil : une phrase de passe (plusieurs mots) est plus sûre et plus facile à retenir
        qu'un mot de passe court et complexe.
      </p>
    );
  }

  const results = PASSWORD_RULES.map((rule) => ({ rule, satisfied: rule.isSatisfied(password, email) }));
  const score = results.filter((result) => result.satisfied).length;
  const tone = METER_TONES[Math.max(score - 1, 0)] ?? METER_TONES[0];

  return (
    <div className="flex flex-col gap-2.5">
      {/* La jauge est un doublon VISUEL de la liste qui suit : `aria-hidden` évite de faire
          annoncer deux fois la même information à un lecteur d'écran. */}
      <div aria-hidden className="flex gap-1.5">
        {PASSWORD_RULES.map((rule, position) => (
          <span
            key={rule.id}
            className={cn(
              'h-1 flex-1 rounded-pill transition-colors',
              position < score ? tone : 'bg-ink-100',
            )}
          />
        ))}
      </div>

      <p className="text-[11px] font-medium text-ink-500">Règles à respecter :</p>

      <ul aria-live="polite" className="flex flex-col gap-1.5">
        {results.map(({ rule, satisfied }) => (
          <li
            key={rule.id}
            className={cn(
              'flex items-center gap-2 text-[11px]',
              satisfied ? 'text-success-500' : 'text-ink-500',
            )}
          >
            {satisfied ? (
              <Check aria-hidden className="size-3.5 shrink-0" />
            ) : (
              <Circle aria-hidden className="size-3.5 shrink-0 text-ink-300" />
            )}
            {/* Le libellé porte l'information ; l'icône n'est qu'un renfort visuel. */}
            <span>{rule.label(password, email)}</span>
            <span className="sr-only">{satisfied ? '— rempli' : '— non rempli'}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
